import { DocumentRecord } from '@synapex/prisma/client'
import { WorkspaceDescriptor } from '@synapex/shared2/entities'
import type {
    GetInvoicesInputDto,
    UploadInvoiceInputDto,
} from '@synapex/shared2/gateway-rest-api'
import {
    useMutation,
    UseMutationOptions,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from '@tanstack/react-query'
import { apiQueryKey } from '../../'
import { OmitWwld } from '../../utils'
import { apiGetInvoices, apiUploadInvoiceFile } from '../api'

export const useGetInvoices = (
    input: GetInvoicesInputDto,
    options?: Omit<UseQueryOptions<DocumentRecord[], Error>, 'queryKey'>
) => {
    return useQuery<DocumentRecord[], Error>({
        queryKey: apiQueryKey.invoices(input, input.contragentDistributorId),
        queryFn: () => apiGetInvoices(input),
        ...options,
    })
}

export const useUploadInvoiceFile = (
    wd: WorkspaceDescriptor,
    options?: Omit<
        UseMutationOptions<
            void,
            Error,
            OmitWwld<UploadInvoiceInputDto> & { file: File }
        >,
        'mutationFn'
    >
) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationKey: ['upload-file'],
        mutationFn: (input: OmitWwld<UploadInvoiceInputDto> & { file: File }) =>
            apiUploadInvoiceFile({
                ...wd,
                ...input,
                fileName: input.file.name,
            }) as Promise<void>,

        onSuccess(_, { ordContragentId, draftContractId, customContractId }) {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.invoices(wd, ordContragentId),
            })

            // Invalidate only marking forms that have matching draftContractId
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(),
                predicate: (query) => {
                    if (!query.state.data) {
                        return false
                    }
                    const queryData = query.state.data as {
                        draft_contract_id?: number
                        custom_contract_id?: number
                    }
                    return (
                        queryData.draft_contract_id === draftContractId ||
                        queryData.custom_contract_id === customContractId
                    )
                },
            })
        },
        ...options,
    })
}
